import {
  getPrimaryCategories,
  getCallOutNudgeText,
  getCallOutTextV2,
} from '@nykaa/product-card-shared/utils';
import { transformPriceFluctuationData } from '@nykaa/ui-kit/component/PriceFluctuationNudge/utils';
import { transformAdPlatformData } from '@nykaa/listing-shared/transformer/index';
import { transformListingGuides } from '@nykaa/listing-shared/transformer/filter';
import { isValidArrayLength } from '@nykaa/utils/validations';
import { transformListingFilters } from 'shared/helpers/filters/transformer';
import { getVariantType } from 'helpers/utils/variantType';
import {
  ListingData,
  ListingProduct,
  TipTile,
  ListingItemObjectType,
  InlineFiltersData,
  InlineFilter,
  Filter,
  InlineGuidesV2,
  EddInfoData,
} from './types';
import transformTipTile from './helpers/transformTipTile';
import { ADD_TO_BAG_TEXT, NO_TAG, contentLang } from 'shared/constants';
import transformPriceRevealData from '@nykaa/listing-shared/transformer/priceReveal';
import { InlineGuidesV2 as InlineMultiWidget } from '@nykaa/inline-filter/components/MultiFilterWidget/types';
import {
  getCohortSaleData,
  getCohortSaleDataV2,
  getUpdatedTemplateV2,
} from 'helpers/utils/store/priceReaveal';

export const transformCustomTag = (newTag: any) =>
  newTag?.map((tag: any) => ({
    bgColor: tag?.bgColor || '',
    borderColor: tag?.borderColor || '',
    fontSize: tag?.fontsize + 'px' || '',
    fontWeight: tag?.fontweight || '',
    title: tag?.title || '',
    titleColor: tag?.titleColor || '',
    opacity: tag?.transparency_web || '',
  }));

export const transformEddInfo = (eddInfo: EddInfoData, imageUrl = '') => {
  if (!eddInfo) {
    return null;
  }
  const { tag = '', name = '', text_color = '', bg_color } = eddInfo;
  return {
    imageUrl,
    tag,
    textColor: text_color,
    text: name,
    bgColor: bg_color,
  };
};

export const transformListingProducts = (
  products: any,
  assetsBucket: any
): (ListingProduct | TipTile)[] => {
  if (!(products && Array.isArray(products))) {
    return [];
  }

  const transformedProducts: (ListingProduct | TipTile)[] = [];

  products.forEach((product) => {
    // HANDLE TIP TILES
    const { object_type: objectType } = product;
    const cohortSaleTemplateV2 = getUpdatedTemplateV2(
      product?.cohort_sale_template,
      product?.cohort_sale_template_v2
    );
    const curationTagsurl: string[] = [];
    if (assetsBucket && product?.curation_tags) {
      product.curation_tags?.map((tag: string) => {
        if (assetsBucket[tag]) {
          curationTagsurl?.push(assetsBucket[tag]);
        }
      });
    }

    const leftTag = product?.left_tags;
    const leftTagUrl = leftTag && assetsBucket?.[leftTag];

    const secondaryTag = product?.secondary_tags;
    const secondaryTagUrl = secondaryTag && assetsBucket?.[secondaryTag];
    const secondaryTagV2 = product?.secondary_tags_v2;
    const secondaryTagV2Url = secondaryTagV2 && assetsBucket?.[secondaryTagV2];
    if (objectType === ListingItemObjectType.PRODUCT) {
      const transformedProduct: ListingProduct = {
        brandIds: product.brand_ids,
        brandName: product.brand_name || '',
        buttonText: product.button_text || ADD_TO_BAG_TEXT,
        newButtonText: product.button_text_new,
        categoryIds: product.category_ids,
        childId: product.id,
        discount: product.discount || 0,
        dynamicTags: product.dynamic_tags || [],
        newTags: transformCustomTag(product.pdt_tags),
        id: product.id,
        imageUrl: product.image_url,
        inStock: product.gludo_stock,
        mrp: product.price,
        name: product.name,
        offersCount: product.offer_count,
        onlyWishlistButton: product.show_wishlist_button,
        parentId: product.parent_id,
        price: product.final_price,
        primaryCategories: getPrimaryCategories(product.primary_categories),
        productId: product.id,
        proFlag: product.pro_flag,
        sku: product.sku,
        quantity: product.quantity,
        packSize: product.pack_size,
        isMini: product.is_mini || false,
        rating: product.rating || 0,
        ratingCount: product.rating_count || 0,
        formattedRatingCount: product.formatted_rating_count,
        slug: product.slug,
        title: product.name, // !FIXME: Adding this due to a redundant prop for Product Card
        type: product.type,
        productTitle: product.product_title,
        variantCount: product.option_count,
        variantType: getVariantType(product.configurable_type).toLowerCase(),
        isBackorder: !!product.is_backorder,
        contentLang: product[contentLang],
        offer: product.offer_message,
        offerColor: product.plp_offer_color,
        offerId: product.offer_id,
        showOffer: product.plp_offer,
        isLux: Number(product.is_lux) || 0,
        tracking_metadata: product.tracking_metadata,
        prData: transformPriceRevealData(
          product.pr_data,
          product.sale_template
        ),
        isPrEnabled: product.pr_enabled_raw || false,
        isEarlyAccess: product.early_access || false,
        isFlashSale: product.flash_sale || false,
        callOutsTemplate: getCallOutNudgeText(product.call_outs_template),
        cohortSaleTemplate: getCohortSaleData(product.cohort_sale_template),
        cohortSaleTemplateV2: getCohortSaleDataV2(
          cohortSaleTemplateV2,
          assetsBucket
        ),
        urgencyTemplate: transformPriceFluctuationData(
          product.urgency_template
        ),
        isBestPriceLoaded: false,
        primaryTags: curationTagsurl,
        leftTags: leftTagUrl,
        leftTagText: leftTag || '',
        secondaryTags: secondaryTagUrl,
        secondaryTagName: product.secondary_tags || NO_TAG,
        callOutsTemplateV2: getCallOutTextV2(product.call_outs_template_v2),
        isOfferPresent: product.isOfferPresent || false,
        siblingColourCodes: product.sibling_colour_codes || [],
        disablePriveProduct: product.disable_prive_product || false,
        eddInfo: transformEddInfo(product.edd_info, assetsBucket?.qc),
        plpPdpBridge: product.plp_pdp_bridge || [],
        secondaryTagV2: secondaryTagV2Url,
        productNudges: product.product_nudges || {},
      };

      transformedProducts.push(transformedProduct);
    } else if (objectType === ListingItemObjectType.TIP_TILE) {
      const transformedTipTile = transformTipTile(product);

      if (transformedTipTile) {
        transformedProducts.push(transformedTipTile);
      }
    }
  });

  return transformedProducts;
};

export const transformFilters = (inlineFilter: InlineFilter) => {
  if (inlineFilter) {
    const {
      bg_color,
      is_multi_select,
      redirect_filter,
      redirect_text,
      title,
      type,
      values,
      name,
      position,
    } = inlineFilter;
    return {
      bgColor: bg_color || '',
      isMultiSelect: is_multi_select || false,
      redirectFilter: redirect_filter || '',
      redirectText: redirect_text || '',
      title: title || '',
      type: type || '',
      name: name || '',
      position: position || null,
      values:
        values?.map((filter: Filter) => ({
          name: filter.name || '',
          id: filter.id || '',
          score: filter.score || '',
        })) || [],
    };
  }
  return {};
};

export const transformInlineFilters = (inlineFilters: InlineFiltersData) => {
  const inlineFilterKeys = Object.keys(inlineFilters);
  const transformedInlineFilter = { ...inlineFilters };

  inlineFilterKeys?.forEach((key: string) => {
    transformedInlineFilter[key] = transformFilters(inlineFilters[key]);
  });
  return transformedInlineFilter;
};

export const transformInlineFiltersV2 = (
  inlineFilters: InlineGuidesV2
): InlineMultiWidget | null => {
  if (!inlineFilters) return null;

  const { positions, parent_title, background_color_url, filters } =
    inlineFilters;

  const transformedFilters =
    filters?.map((filter) => {
      const {
        type,
        name,
        title,
        is_multi_select: isMultiSelect,
        bg_color: bgColor,
        redirect_filter: redirectFilter,
        redirect_text: redirectText,
        option_icon_type: optionIconType,
        values,
        position,
      } = filter;

      return {
        type: type || '',
        name: name || '',
        title: title || '',
        isMultiSelect: isMultiSelect || false,
        bgColor: bgColor || '',
        redirectFilter: redirectFilter || '',
        redirectText: redirectText || '',
        optionIconType: optionIconType || 'TEXT',
        position: position || null,
        values:
          values?.map((value) => ({
            name: value.name || '',
            id: value.id || '',
            score: value.score || '',
            imageUrl: value.image_url || '',
            colorCode: value.color_code || '',
          })) || [],
      };
    }) || [];

  return {
    positions: positions || [],
    parentTitle: parent_title || '',
    backgroundColorUrl: background_color_url || '',
    filters: transformedFilters,
  };
};

export const transformProductListingData = (data: any): ListingData => {
  const {
    category_name: categoryName,
    meta_description: metaDescription,
    meta_keywords: metaKeywords,
    meta_title: metaTitle,
    products,
    filters,
    sort,
    total_found: totalProducts,
    assets_bucket: assetsBucket,
    stop_further_call,
    url: canonical,
    type,
    guides,
    guides_v2,
    new_ui,
    review_rating_visible,
    cta_visible,
    image_aspect_ratio,
    inline_guides,
    brand_name: brandName,
    ad_platform_data,
    canonical: canonicalUrl = '',
    inline_guides_v2: inlineGuidesV2,
    has_edd: hasEdd = false,
    visual_filters,
  } = data;
  const transformedFilters = transformListingFilters(filters);
  const transformedData: ListingData = {
    categoryName,
    metaDescription,
    metaKeywords,
    canonical,
    metaTitle,
    products: transformListingProducts(products, assetsBucket) || [],
    sort,
    totalProducts,
    trackingMetadata: data.tracking_metadata || {},
    totalFound: totalProducts,
    assetsBucket,
    filters: transformListingFilters(filters),
    stopFurtherCall: !!stop_further_call,
    listingType: type,
    guides: transformListingGuides(guides, transformedFilters),
    guidesV2: transformListingGuides(guides_v2, transformedFilters),
    newUi: new_ui || false,
    reviewRatingVisible: review_rating_visible,
    ctaVisible: cta_visible,
    imageAspectRatio: image_aspect_ratio || 1,
    inlineFilters: transformInlineFilters(inline_guides || {}),
    brandName,
    inlineWidgetConfig: transformAdPlatformData(ad_platform_data),
    canonicalUrl,
    inlineFiltersV2: transformInlineFiltersV2(inlineGuidesV2 || {}),
    hasEdd,
    visualFilters: visual_filters || [],
  };

  return transformedData;
};

export const transformPriceListBestSellers = (list) =>
  list.map((item) => ({
    inStock: item.in_stock,
    mrp: item.mrp,
    productName: item.product_name,
    productUrl: item.product_url,
    quantity: item.quantity,
  }));

export const transformPriceList = (data) => {
  if (!data) {
    return {};
  }
  return {
    bestSellers: transformPriceListBestSellers(data.best_sellers || []),
    title: data.title,
    lastUpdated: data.last_updated,
  };
};

export const transformAboutUs = (data) => {
  if (!data) {
    return {};
  }
  return {
    brandInfo: data.brand_info,
  };
};

export const transformChildGuideValues = (data) => {
  if (!isValidArrayLength(data)) {
    return [];
  }
  return data?.map((guide) => {
    const { id, image_url, name, count } = guide;
    return {
      id,
      imageUrl: image_url || '',
      name,
      count,
    };
  });
};

export const transformEddFilterList = (data) => {
  if (!data) {
    return {};
  }

  const {
    bg_color_deselcted,
    bg_color_selected_web,
    border_color_deselected,
    border_color_selected,
    id,
    image_url,
    name,
    score,
    type,
    child_values,
    selection,
  } = data;

  return {
    key: type,
    type: selection,
    data: {
      bgColorDeselcted: bg_color_deselcted || '',
      bgColorSelected: bg_color_selected_web || '',
      colorSelected: border_color_selected || '',
      colorDeselected: border_color_deselected || '',
      imageUrl: image_url || '',
      id,
      name,
      score,
      childValues: transformChildGuideValues(child_values),
      selection: selection || null,
    },
  };
};
